import type { EntityState } from "@reduxjs/toolkit";
import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import type {
	GetDefaultOrganizationSettingsRequest,
	GetDefaultOrganizationSettingsResponse,
	UpdateDefaultOrganizationSettingsResponse,
} from "@somewear/api/src/proto/api/organization_pb";
import type { OrganizationDefaultSettings } from "@somewear/api/src/proto/organization_default_settings_proto_pb";
import { createActionSet } from "@somewear/model";
import type { WritableDraft } from "immer/dist/types/types-external";

import type { OrganizationWorkspaceDefaultsRequestSchema } from "./organization.model";
import type { OrganizationState } from "./organization.state";

export interface IOrganizationWorkspaceSettingsState extends OrganizationDefaultSettings.AsObject {
	organizationId: string;
}

export const organizationDeviceSettingsActions = {
	updateDefaultDeviceSettings: createActionSet<
		OrganizationWorkspaceDefaultsRequestSchema,
		UpdateDefaultOrganizationSettingsResponse.AsObject
	>("organization/defaultSettings/update"),
	getDefaultDeviceSettings: createActionSet<
		GetDefaultOrganizationSettingsRequest.AsObject,
		GetDefaultOrganizationSettingsResponse.AsObject
	>("organization/defaultSettings/get"),
};

const adapter = createEntityAdapter<IOrganizationWorkspaceSettingsState>({
	selectId: (org) => org.organizationId,
});

export const {
	selectAll: selectAllOrganizationDeviceSettings,
	selectById: selectOrganizationDeviceSettingsById,
} = adapter.getSelectors((state: OrganizationState) => state.organizationDeviceSettings);

function updateSettings(
	state: WritableDraft<EntityState<IOrganizationWorkspaceSettingsState>>,
	payload: GetDefaultOrganizationSettingsResponse.AsObject,
): IOrganizationWorkspaceSettingsState | undefined {
	if (!payload.organizationId || !payload.settings || !organizationHasSettings(payload)) return;

	adapter.upsertOne(state, {
		organizationId: payload.organizationId,
		...payload.settings,
	});
}

function organizationHasSettings(
	response: GetDefaultOrganizationSettingsResponse.AsObject,
): boolean {
	return Boolean(response.settings?.sentTimestamp && response.settings.sentTimestamp > 0);
}

export const organizationDefaultWorkspaceSettingsSlice = createSlice({
	name: "organizationDefaultWorkspaceSettings",
	initialState: adapter.getInitialState(),
	reducers: {
		_: () => {},
	},
	extraReducers: (builder) => {
		builder.addCase(
			organizationDeviceSettingsActions.getDefaultDeviceSettings.fulfilled,
			(state, action) => {
				updateSettings(state, action.payload.data);
			},
		);
		builder.addCase(
			organizationDeviceSettingsActions.updateDefaultDeviceSettings.fulfilled,
			(state, action) => {
				updateSettings(state, action.payload.data);
			},
		);
	},
});
